import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { accounts } from '../services/api.js';
import { getRiskBadgeClass } from '../utils/riskUtils';
import './AccountPage.css';

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatDateTime(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

const TABS = ['Overview', 'Submissions', 'Movement', 'Documents', 'Communications'];

export function AccountPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [accountData, setAccountData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('Overview');

  const loadAccount = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await accounts.get(id);
      const data = result.data || result;
      setAccountData(data);
    } catch (err) {
      setError(err.message || 'Failed to load account');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadAccount();
  }, [loadAccount]);

  const tabLoading = false;

  if (loading) {
    return (
      <div className="ap-loading">
        <div className="spinner" />
        <p>Loading account...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ap-error">
        <h2>Error</h2>
        <p>{error}</p>
        <button className="btn btn--primary" onClick={loadAccount}>
          Retry
        </button>
      </div>
    );
  }

  if (!accountData) return null;

  // The getAccount endpoint returns: { account, submissions, insured, documents, communications, movements, quotes, BrokerName }
  const acct = accountData.account || accountData;
  const insured = accountData.insured || null;
  const acctSubmissions = Array.isArray(accountData.submissions) ? accountData.submissions : [];
  const movement = Array.isArray(accountData.movements) ? accountData.movements : [];
  const docs = Array.isArray(accountData.documents) ? accountData.documents : [];
  const comms = Array.isArray(accountData.communications) ? accountData.communications : [];
  const acctQuotes = Array.isArray(accountData.quotes) ? accountData.quotes : [];
  const brokerName = accountData.BrokerName || acct.BrokerID || '—';

  return (
    <div className="ap">
      {/* Breadcrumb */}
      <div className="ap__breadcrumb">
        <button className="ap__back" onClick={() => navigate('/accounts')}>
          &larr; Back to Accounts
        </button>
      </div>

      {/* Header */}
      <div className="ap__header card">
        <h1 className="ap__name">{acct.InsuredName || 'Unknown Account'}</h1>
        <div className="ap__header-meta">
          <span>
            Account ID: <strong>{acct.AccountID || '—'}</strong>
          </span>
          <span>
            Broker: <strong>{brokerName}</strong>
          </span>
          <span>
            Status: <strong className="ap__status-label">{acct.Status || '—'}</strong>
          </span>
          <span>
            Created: <strong>{formatDate(acct.CreatedAt)}</strong>
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div className="ap__tabs">
        {TABS.map((tab) => (
          <button
            key={tab}
            className={`ap__tab ${activeTab === tab ? 'ap__tab--active' : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="ap__content card">
        {activeTab === 'Overview' && (
          <div className="ap__overview">
            <div className="ap__info-section">
              <h3 className="ap__section-title">Account Information</h3>
              <dl className="ap__info-grid">
                <div className="ap__info-item">
                  <dt>Insured Name</dt>
                  <dd>{acct.InsuredName || '—'}</dd>
                </div>
                <div className="ap__info-item">
                  <dt>Broker</dt>
                  <dd>{brokerName}</dd>
                </div>
                <div className="ap__info-item">
                  <dt>Status</dt>
                  <dd style={{ textTransform: 'capitalize' }}>{(acct.Status || '—').replace(/_/g, ' ')}</dd>
                </div>
                <div className="ap__info-item">
                  <dt>Submissions</dt>
                  <dd>{acctSubmissions.length}</dd>
                </div>
                <div className="ap__info-item">
                  <dt>Created</dt>
                  <dd>{formatDate(acct.CreatedAt)}</dd>
                </div>
                <div className="ap__info-item">
                  <dt>Last Updated</dt>
                  <dd>{formatDate(acct.UpdatedAt)}</dd>
                </div>
              </dl>
            </div>

            {insured && (
              <div className="ap__info-section">
                <h3 className="ap__section-title">Insured Details</h3>
                <dl className="ap__info-grid">
                  <div className="ap__info-item">
                    <dt>Company Name</dt>
                    <dd>{insured.Name || acct.InsuredName || '—'}</dd>
                  </div>
                  {insured.Industry && (
                    <div className="ap__info-item">
                      <dt>Industry</dt>
                      <dd style={{ textTransform: 'capitalize' }}>{insured.Industry}</dd>
                    </div>
                  )}
                  {insured.Revenue > 0 && (
                    <div className="ap__info-item">
                      <dt>Annual Revenue</dt>
                      <dd>{new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(insured.Revenue)}</dd>
                    </div>
                  )}
                  {insured.EmployeeCount > 0 && (
                    <div className="ap__info-item">
                      <dt>Employees</dt>
                      <dd>{new Intl.NumberFormat('en-US').format(insured.EmployeeCount)}</dd>
                    </div>
                  )}
                  {insured.Website && (
                    <div className="ap__info-item">
                      <dt>Website</dt>
                      <dd>{insured.Website}</dd>
                    </div>
                  )}
                  <div className="ap__info-item">
                    <dt>Attested</dt>
                    <dd>{insured.Attested ? 'Yes' : 'No'}</dd>
                  </div>
                </dl>
              </div>
            )}

            {acctQuotes.length > 0 && (
              <div className="ap__info-section">
                <h3 className="ap__section-title">Quotes</h3>
                <table className="ap__sub-table">
                  <thead>
                    <tr>
                      <th>Premium</th>
                      <th>Deductible</th>
                      <th>Limit</th>
                      <th>Status</th>
                      <th>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {acctQuotes.map((q) => (
                      <tr key={q.QuoteID}>
                        <td>{new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(q.Premium)}</td>
                        <td>{new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(q.Deductible)}</td>
                        <td>{new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(q.Limit)}</td>
                        <td style={{ textTransform: 'capitalize' }}>{q.Status || '—'}</td>
                        <td>{formatDate(q.CreatedAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'Submissions' && (
          <div className="ap__submissions">
            <h3 className="ap__section-title">Submissions</h3>
            {tabLoading ? (
              <div className="ap__tab-loading">
                <div className="spinner" />
              </div>
            ) : Array.isArray(acctSubmissions) && acctSubmissions.length > 0 ? (
              <table className="ap__sub-table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Underwriter</th>
                    <th>Risk</th>
                    <th>Status</th>
                    <th>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {acctSubmissions.map((sub) => (
                    <tr
                      key={sub.SubmissionID}
                      className="ap__sub-row"
                      onClick={() => navigate(`/submissions/${sub.SubmissionID}`)}
                    >
                      <td className="ap__sub-product">{sub.ProductName || sub.ProductID || '—'}</td>
                      <td>{sub.AssignedUWName || sub.AssignedUW || '—'}</td>
                      <td>
                        <span className={getRiskBadgeClass(sub.RiskTier || sub.RiskScore)}>
                          {sub.RiskScore != null ? sub.RiskScore : '—'}
                          {sub.RiskTier && sub.RiskTier !== 'unscored'
                            ? ` · ${sub.RiskTier}`
                            : ''}
                        </span>
                      </td>
                      <td>
                        <span className="ap__sub-status" data-status={sub.Status}>
                          {(sub.Status || 'new').replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="ap__sub-date">{formatDate(sub.CreatedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="ap__empty">No submissions for this account.</p>
            )}
          </div>
        )}

        {activeTab === 'Movement' && (
          <div className="ap__movement">
            <h3 className="ap__section-title">
              Broker of Record / Agent of Record Timeline
            </h3>
            {tabLoading ? (
              <div className="ap__tab-loading">
                <div className="spinner" />
              </div>
            ) : Array.isArray(movement) && movement.length > 0 ? (
              <div className="ap__timeline">
                {movement.map((entry, idx) => (
                  <div key={idx} className="ap__timeline-item">
                    <div className="ap__timeline-marker">
                      <div className="ap__timeline-dot" />
                      {idx < movement.length - 1 && <div className="ap__timeline-line" />}
                    </div>
                    <div className="ap__timeline-content">
                      <div className="ap__timeline-header">
                        <strong>{entry.MovementType || 'Transition'}</strong>
                        <span className="ap__timeline-date">
                          {formatDateTime(entry.CreatedAt)}
                        </span>
                      </div>
                      <p className="ap__timeline-detail">
                        {entry.FromBrokerName || entry.FromBrokerID
                          ? `${entry.FromBrokerName || entry.FromBrokerID} → ${entry.ToBrokerName || entry.ToBrokerID}`
                          : '—'}
                      </p>
                      {entry.Reason && (
                        <p className="ap__timeline-reason">Reason: {entry.Reason}</p>
                      )}
                      {entry.RecordedBy && (
                        <p className="ap__timeline-reason">Recorded by: {entry.RecordedBy}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="ap__empty">No broker movement history available.</p>
            )}
          </div>
        )}

        {activeTab === 'Documents' && (
          <div className="ap__documents">
            <h3 className="ap__section-title">Documents</h3>
            {tabLoading ? (
              <div className="ap__tab-loading">
                <div className="spinner" />
              </div>
            ) : Array.isArray(docs) && docs.length > 0 ? (
              <table className="ap__doc-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Uploaded</th>
                  </tr>
                </thead>
                <tbody>
                  {docs.map((doc) => (
                    <tr key={doc.DocumentID || doc.SK}>
                      <td className="ap__doc-name">{doc.FileName || doc.Name || '—'}</td>
                      <td>{doc.DocumentType || doc.Type || '—'}</td>
                      <td>{formatDate(doc.CreatedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="ap__empty">No documents for this account.</p>
            )}
          </div>
        )}

        {activeTab === 'Communications' && (
          <div className="ap__comms">
            <h3 className="ap__section-title">Communication History</h3>
            {tabLoading ? (
              <div className="ap__tab-loading">
                <div className="spinner" />
              </div>
            ) : Array.isArray(comms) && comms.length > 0 ? (
              <div className="ap__comms-list">
                {comms.map((msg, idx) => (
                  <div key={msg.CommunicationID || idx} className="ap__comm-item">
                    <div className="ap__comm-header">
                      <strong>{msg.FromUserID || 'System'}</strong>
                      <span className="ap__comm-date">
                        {formatDateTime(msg.SentAt || msg.CreatedAt)}
                      </span>
                    </div>
                    {msg.Subject && <p className="ap__comm-subject">{msg.Subject}</p>}
                    <p className="ap__comm-body">{msg.Body || ''}</p>
                    {Array.isArray(msg.ToEmails) && msg.ToEmails.length > 0 && (
                      <p className="ap__comm-body" style={{ fontSize: '0.85em', opacity: 0.7 }}>
                        To: {msg.ToEmails.join(', ')}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="ap__empty">No communications for this account.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
